import { defineCollection } from "@rebasepro/cms-types";
import { policy } from "@rebasepro/types";

/**
 * What every member's wallet card shares, editable from the admin: the
 * programme's name, the brand, the colour, the logos, and the texts and links
 * in the card's details (the programme's story, the rules, the account page).
 * One row; the backend reads the oldest. Both wallets read it: Google from the
 * google_* fields, Apple from the apple_* ones, and both from the rest.
 *
 * Saving it rewrites the programme in Google Wallet, and every card already
 * saved shows the change (backend/src/google-card.ts, refreshGoogleClass); the
 * iPhones holding an Apple card are told to download it again
 * (backend/src/apple-card.ts, pushAllAppleCards).
 *
 * Not here, on purpose: the Google issuer id and class suffix, and the service
 * account key. They say which environment this is, and staging is a copy of
 * the production database: carried in it, they would point staging at
 * production's cards. They stay in each environment's settings.
 */
const walletSettingsCollection = defineCollection({
    name: "Wallet Settings",
    singularName: "Wallet Settings",
    slug: "wallet_settings",
    table: "wallet_settings",
    description: "How the wallet card looks and what it says, for every member. One row.",
    history: true,
    securityRules: [
        { operations: ["select", "insert", "update", "delete"], roles: ["admin"] },
        // The wallet functions read it, and the class refresh runs, outside any
        // signed-in user.
        { operations: ["select", "insert", "update", "delete"], condition: policy.serverContext() }
    ],
    callbacks: {
        afterSave: async ({ values }) => {
            // Same in-process route as the card pushes (functions/wallet.ts).
            // Fire-and-forget: the setting is saved whatever Google answers,
            // and the next save tries again.
            const row = values as Record<string, unknown>;
            const savedAt = row.updated_at ?? row.updatedAt ?? row.created_at ?? row.createdAt;
            const savedDate = savedAt ? new Date(savedAt as string) : null;
            const since = savedDate && !Number.isNaN(savedDate.getTime()) ? savedDate.toISOString() : undefined;
            type ClassRefresh = (since?: string) => Promise<string>;
            const walletPush = (globalThis as Record<symbol, unknown>)[Symbol.for("unfeigned-loyalty.wallet-push")] as
                Record<string, ClassRefresh | undefined> | undefined;
            walletPush?.googleClass?.(since).catch((err) => {
                console.error("[Google Wallet] Failed to update the programme:", err);
            });
            walletPush?.appleAll?.(since).catch((err) => {
                console.error("[Apple Wallet] Failed to tell the iPhones about the new programme:", err);
            });
        }
    },
    properties: {
        id: {
            name: "ID",
            type: "number",
            isId: "increment"
        },
        google_program_name: {
            name: "Programme name",
            type: "string",
            description: "The card's title, e.g. \"the kit\"."
        },
        google_issuer_name: {
            name: "Brand",
            type: "string",
            description: "Who issues the card, e.g. \"unfeigned\". Google recommends 20 characters at most."
        },
        google_background_color: {
            name: "Card colour",
            type: "string",
            validation: { matches: /^#[0-9a-fA-F]{6}$/, matchesMessage: "A colour as #rrggbb, e.g. #FFFFFF" },
            description: "One colour for every card: Google has no colour per member."
        },
        google_logo_url: {
            name: "Logo",
            type: "string",
            url: true,
            admin: { urlPreview: "image" },
            description: "PNG on HTTPS, square, 660 × 660 px or more with a 15 % margin: Google shows it in a circle."
        },
        google_wide_logo_url: {
            name: "Wide logo",
            type: "string",
            url: true,
            admin: { urlPreview: "image" },
            description: "The wordmark, PNG on HTTPS, 1280 × 400 px. On Android it runs across the top of the card."
        },
        google_wide_logo_dark_url: {
            name: "Wide logo, dark cards",
            type: "string",
            url: true,
            admin: { urlPreview: "image" },
            description: "The same wide logo in white, PNG on HTTPS, 1280 × 400 px: Google's dark levels (MODERN " +
                "MIND) show it instead of the dark one, which would vanish into the card."
        },
        apple_icon_url: {
            name: "Apple icon",
            type: "string",
            url: true,
            admin: { urlPreview: "image" },
            description: "Square PNG on HTTPS, shown in the iPhone's notifications and lock screen. Without it, the " +
                "Google logo is used if it is a PNG."
        },
        apple_logo_url: {
            name: "Apple logo, light cards",
            type: "string",
            url: true,
            admin: { urlPreview: "image" },
            description: "The wordmark as a transparent PNG on HTTPS, dark ink, for the white cards. Top left of the " +
                "Apple card."
        },
        apple_logo_dark_url: {
            name: "Apple logo, dark cards",
            type: "string",
            url: true,
            admin: { urlPreview: "image" },
            description: "The same wordmark in white, for a dark card (shell). Without it, a dark card shows the brand " +
                "name as text."
        },
        card_texts: {
            name: "Texts",
            type: "array",
            columnType: "jsonb",
            description: "Shown in every card's details, in this order: the programme's story, the rules.",
            of: {
                name: "Text",
                type: "map",
                properties: {
                    title: { name: "Title", type: "string", validation: { required: true } },
                    body: { name: "Text", type: "string", validation: { required: true }, admin: { multiline: true } }
                },
                propertiesOrder: ["title", "body"]
            }
        },
        card_links: {
            name: "Links",
            type: "array",
            columnType: "jsonb",
            description: "Shown in every card's details: the programme page, the account, the shops.",
            of: {
                name: "Link",
                type: "map",
                properties: {
                    label: { name: "Label", type: "string", validation: { required: true } },
                    url: { name: "Link", type: "string", url: true, validation: { required: true } }
                },
                propertiesOrder: ["label", "url"]
            }
        },
        card_actions: {
            name: "Actions",
            type: "array",
            columnType: "jsonb",
            description: "Buttons on the card, in this order: \"tienda más cercana\", \"la colección\". With only one, Google " +
                "shows it on the front of the card. Ten at most.",
            of: {
                name: "Action",
                type: "map",
                properties: {
                    title: { name: "Title", type: "string", validation: { required: true }, description: "60 characters at most." },
                    text: { name: "Text", type: "string", description: "Optional, 50 characters at most." },
                    url: { name: "Link", type: "string", url: true, validation: { required: true } },
                    image_url: { name: "Image", type: "string", url: true, description: "Optional, a square PNG on HTTPS." }
                },
                propertiesOrder: ["title", "text", "url", "image_url"]
            }
        },
        created_at: {
            name: "Created At",
            type: "date",
            mode: "date_time",
            autoValue: "on_create",
            admin: { readOnly: true }
        },
        updated_at: {
            name: "Updated At",
            type: "date",
            mode: "date_time",
            autoValue: "on_update",
            admin: { readOnly: true }
        }
    },
    admin: {
        icon: "Settings",
        group: "Loyalty",
        defaultViewMode: "list",
        enabledViews: ["list"],
        listProperties: ["google_program_name", "google_issuer_name"],
        openEntityMode: "side_panel",
        entityActions: [
            {
                // Saving the row already does this. The button is for when
                // there is nothing to save: the first push, or an image
                // replaced at the same address.
                name: "Actualizar en Google",
                icon: "RefreshCw",
                onClick: async ({ context }) => {
                    if (!context) return;
                    const functions = context.client.functions;
                    if (!functions) {
                        context.snackbarController?.open({
                            type: "error",
                            message: "Actualizar en Google: este cliente no puede llamar a las funciones del servidor."
                        });
                        return;
                    }
                    try {
                        await functions.invoke("wallet", undefined, { method: "POST", path: "google/class" });
                        context.snackbarController?.open({
                            type: "success",
                            message: "Programa actualizado en Google. Las tarjetas guardadas lo muestran al refrescarse."
                        });
                    } catch (error) {
                        context.snackbarController?.open({
                            type: "error",
                            message: `Actualizar en Google: ${error instanceof Error ? error.message : String(error)}`
                        });
                    }
                }
            }
        ],
        propertiesOrder: [
            "google_program_name", "google_issuer_name", "google_background_color",
            "google_logo_url", "google_wide_logo_url", "google_wide_logo_dark_url",
            "apple_icon_url", "apple_logo_url", "apple_logo_dark_url",
            "card_texts", "card_links", "card_actions",
            "created_at", "updated_at"
        ]
    }
});

export default walletSettingsCollection;
